import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Link } from 'react-router-dom';
import { Flower, ShoppingBag, User, LogOut, ShieldAlert, Sparkles, Wand2 } from 'lucide-react';

// View Components
import Home from './components/Home';
import Login from './components/Login';
import Register from './components/Register';
import Customize from './components/Customize';
import Cart from './components/Cart';
import Profile from './components/Profile';
import AdminDashboard from './components/AdminDashboard';

// Database & State Subscriptions
import { mockAuth, subscribeToDB } from './services/mockDatabase';
import './index.css';

// Hook wrapper for URL parameters in route components (CRA template compatibility)
import { useParams } from 'react-router-dom';

const CustomizeWrapper = ({ onAddToCart }) => {
  const { id } = useParams();
  return <Customize productId={id} onAddToCart={onAddToCart} />;
};

function App() {
  const [user, setUser] = useState(null);
  const [cart, setCart] = useState([]);

  // Load User & Cart on start
  useEffect(() => {
    setUser(mockAuth.getCurrentUser());
    
    // Load persisted cart
    const savedCart = localStorage.getItem('bb_cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }

    // Subscribe to database changes (e.g. login/logout from another view)
    const unsubscribe = subscribeToDB(() => {
      setUser(mockAuth.getCurrentUser());
    });
    return unsubscribe;
  }, []);

  // Sync cart to localStorage on changes
  const saveCart = (updatedCart) => {
    setCart(updatedCart);
    localStorage.setItem('bb_cart', JSON.stringify(updatedCart));
  };

  // Cart Handlers
  const handleAddToCart = (item) => {
    const existingIdx = cart.findIndex(c => c.id === item.id);
    if (existingIdx > -1) {
      const updated = [...cart];
      updated[existingIdx].quantity += item.quantity;
      saveCart(updated);
    } else {
      saveCart([...cart, item]);
    }
  };

  const handleUpdateQuantity = (id, quantity) => {
    const updated = cart.map(c => c.id === id ? { ...c, quantity } : c);
    saveCart(updated);
  };

  const handleRemoveFromCart = (id) => {
    const updated = cart.filter(c => c.id !== id);
    saveCart(updated);
  };

  const handleClearCart = () => {
    saveCart([]);
  };

  const handleLogout = () => {
    mockAuth.logout();
    setUser(null);
    window.location.hash = '#/';
  };

  const totalItemsCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <HashRouter>
      <div className="min-h-screen bg-pink-50 font-sans flex flex-col">
        
        {/* Navigation Bar */}
        <nav className="p-4 md:p-6 flex flex-col sm:flex-row justify-between items-center bg-white shadow-sm border-b border-pink-100 gap-4 sticky top-0 z-50">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-2xl font-black text-pink-600 flex items-center gap-2 drop-shadow-sm hover:scale-102 transition-transform">
              <Flower className="text-yellow-400 animate-spin-slow" />
              Georgia's Bead Boutique
            </Link>
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            {/* Quick Special Order Link */}
            <Link 
              to="/customize"
              className="flex items-center gap-1 text-xs md:text-sm font-bold text-purple-600 bg-purple-50 hover:bg-purple-100 px-3.5 py-2 rounded-full transition"
            >
              <Wand2 size={14} /> Request Custom Design
            </Link>

            {/* Shopping Cart Button */}
            <Link 
              to="/cart" 
              className="bg-pink-500 text-white px-4 py-2 rounded-full flex items-center gap-2 hover:bg-pink-600 shadow-md hover:shadow-lg transition text-sm font-bold"
            >
              <ShoppingBag size={18} />
              <span>Cart ({totalItemsCount})</span>
            </Link>

            {/* Auth / Dashboards */}
            {user ? (
              <div className="flex items-center gap-2.5">
                <Link 
                  to={user.role === 'admin' ? '/admin' : '/profile'} 
                  className="flex items-center gap-1.5 text-sm font-bold text-gray-700 hover:text-pink-600 transition"
                >
                  <User size={16} />
                  <span>{user.displayName}</span>
                  {user.role === 'admin' && (
                    <span className="text-[10px] bg-red-100 text-red-600 font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5">
                      <ShieldAlert size={10} /> Owner
                    </span>
                  )}
                </Link>
                <button 
                  onClick={handleLogout}
                  className="p-2 text-gray-400 hover:text-pink-600 transition"
                  title="Sign Out"
                >
                  <LogOut size={16} />
                </button>
              </div>
            ) : (
              <Link 
                to="/login" 
                className="text-sm font-bold text-gray-600 hover:text-pink-500 transition px-2"
              >
                Sign In
              </Link>
            )}
          </div>
        </nav>

        {/* Main Content Area */}
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<Home onAddToCart={handleAddToCart} />} />
            <Route path="/login" element={<Login onLoginSuccess={setUser} />} />
            <Route path="/register" element={<Register onLoginSuccess={setUser} />} />
            <Route path="/customize" element={<Customize onAddToCart={handleAddToCart} />} />
            <Route path="/customize/:id" element={<CustomizeWrapper onAddToCart={handleAddToCart} />} />
            <Route path="/cart" element={<Cart cart={cart} onUpdateQuantity={handleUpdateQuantity} onRemoveFromCart={handleRemoveFromCart} onClearCart={handleClearCart} />} />
            <Route path="/profile" element={<Profile onAddToCart={handleAddToCart} />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="*" element={<Home onAddToCart={handleAddToCart} />} />
          </Routes>
        </main>

        {/* Global Footer */}
        <footer className="bg-gray-50 py-12 px-6 text-center border-t border-gray-200">
          <p className="text-gray-500 font-medium tracking-widest text-xs uppercase flex items-center justify-center gap-1">
            <Sparkles className="text-yellow-400" size={14} /> Owned & Operated by Benchi Labs LLC
          </p>
          <p className="text-gray-400 text-xs italic mt-1.5">
            (a family project run with love by Georgia's mom & dad)
          </p>
          <p className="text-gray-400 text-xs mt-3">
            © 2026 Georgia's Bead Boutique Shop | Made with persistence in St. Peters, MO
          </p>
        </footer>
      </div>
    </HashRouter>
  );
}

export default App;
